<template>
  <div class="converged-workbench">
    <workbench-canvas
      :copiedTarget="copiedTarget"
      :viewOnly="viewOnly"
    >
      <template
        slot="main-container"
        slot-scope="workbenchSlottedValue"
      >
        <template v-if="noSelectedDatasetFamily">
          <div
            class="text-h4 text-md-center"
            style="margin-top: 20vh"
          >
            <p>Graphs will be available when you select at least one attribute</p>
          </div>
        </template>
        <template v-else-if="showInsights">
          <insights
            :workbenchSlottedValue="workbenchSlottedValue.slottedValue"
            :viewOnly="viewOnly"
          />
        </template>
        <template v-else-if="selectedLasDatasetFamily">
          <las-graphs :workbenchSlottedValue="workbenchSlottedValue.slottedValue" />
        </template>
      </template>
      <template
        slot="bottom-pane"
        slot-scope="bottomPane"
      >
        <bottom-panel
          :targetData="bottomPane.copiedTarget"
          :viewOnly="viewOnly"
          :xandrActions="noSelectedDatasetFamily || showInsights"
          :lasActions="selectedLasDatasetFamily"
        />
      </template>
    </workbench-canvas>
    <save-workbench-changes-modal
      ref="saveWorkbenchChangesModal"
      :newXandrTarget="copiedTarget"
    />
  </div>
</template>

<script>
  import Insights from '@/appComponents/workbench/insights/Insights'
  import LasGraphs from '@/appComponents/workbench/lasGraphs/LasGraphs.vue'
  import BottomPanel from '@/appComponents/workbench/bottomPanel/BottomPanel'
  import SaveWorkbenchChangesModal from '@/appComponents/workbench/saveChanges/SaveWorkbenchChangesModal.vue'
  import WorkbenchCanvas from '@/appComponents/workbench/WorkbenchCanvas'
  import { cloneDeep } from 'lodash'
  import { ACTIVATION_STATUS, DESTINATIONS } from '@/appLogic/targets/Constants'
  import { ON_CREATE_ACTION, RESET_STORE } from '@/appLogic/workbench/common/store/actions'
  import { SET_CURRENT_TARGET, DELETE_CURRENT_TARGET } from '@/appLogic/targets/currentTarget/store/actions'
  import { deleteTarget } from '@/api'

  export default {
    components: {
      Insights,
      LasGraphs,
      BottomPanel,
      SaveWorkbenchChangesModal,
      WorkbenchCanvas
    },
    data() {
      return {
        interface: null,
        copiedTarget: undefined,
        availableDestinations: [],
      }
    },
    created() {
      const targetId = this.$route.params.id
      this.$store.dispatch('workbench/' + ON_CREATE_ACTION, { interface: this.interface, id: targetId })
    },
    destroyed() {
      this.$store.dispatch('workbench/' + RESET_STORE)
      this.copiedTarget = undefined
    },
    computed: {
      advertiser() {
        return this.originalTarget ? this.originalTarget.advertiser : null
      },
      targetName() {
        return this.originalTarget ? this.originalTarget.display_name : ''
      },
      C360Datasources() {
        return this.$store.getters.getC360Datasources
      },
      selectedLasDatasetFamily() {
        let df = this.selectedDatasetFamily
        return df === 'amrld' || df === 'mri' || df === 'ncs'
      },
      selectedDatasetFamily() {
        return this.$store.getters['workbench/selectedFamilyDataset']
      },
      noSelectedDatasetFamily() {
        return !this.selectedDatasetFamily
      },
      showInsights() {
        return Object.values(this.C360Datasources).includes(this.selectedDatasetFamily)
      },
      as() {
        return this.originalTarget && this.originalTarget.activation_status
          ? this.originalTarget.activation_status
          : undefined
      },
      viewOnly() {
        let validStatus =
          [ACTIVATION_STATUS.WORKING, ACTIVATION_STATUS.DRAFT, ACTIVATION_STATUS.FAILED].indexOf(this.as) < 0
        const isModeled = this.isModeling || this.isModeled || this.isModeledFailed
        const isLASAndFailed = this.selectedLasDatasetFamily && this.as === ACTIVATION_STATUS.FAILED
        return (
          !this.hasPermissions ||
          this.isCancelled ||
          isModeled ||
          validStatus ||
          isLASAndFailed ||
          !this.$store.getters.hasCreateEditPermissions ||
          this.$store.state.workbench.loadingWorkbench
        )
      },
      originalTarget() {
        let wt = this.$store.state.workbench.workbenchTarget
        return wt === null ? undefined : wt
      },
      isSubmitted() {
        return this.as === ACTIVATION_STATUS.SUBMITTED
      },
      isFailed() {
        return this.as === ACTIVATION_STATUS.FAILED
      },
      isLive() {
        return this.as === ACTIVATION_STATUS.LIVE
      },
      isModeling() {
        return this.as === ACTIVATION_STATUS.MODELING
      },
      isModeled() {
        return this.as === ACTIVATION_STATUS.MODELED
      },
      isCancelled() {
        return (
          this.originalTarget &&
          this.originalTarget.activation_status &&
          this.originalTarget.activation_status === ACTIVATION_STATUS.CANCELLED
        )
      },
      isSDTarget() {
        return (
          this.originalTarget &&
          this.originalTarget.destinations &&
          (this.originalTarget.destinations.includes(DESTINATIONS.KRUX) ||
            this.originalTarget.destinations.includes(DESTINATIONS.LIVERAMP))
        )
      },
      hasPermissions() {
        if (this.originalTarget && this.originalTarget.destinations) {
          return (this.originalTarget.destinations || []).every(destination => {
            switch (destination) {
              case DESTINATIONS.KRUX:
              case DESTINATIONS.LIVERAMP:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.SYMPHONY:
              case DESTINATIONS.FORESITE:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.DIRECTV:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.VIZIO:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.CANOE_LINEAR:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.CANOE_VOD:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.FREEWHEEL:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.GOOGLE_AD_MANAGER:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.VIDEOAMP:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.FREEWHEEL_AUDIENCE_MANAGER:
                return this.$store.getters.hasCreateEditPermissions
              default:
                return false
            }
          })
        } else {
          return false
        }
      },
    },
    mounted() {
      this.copyTarget()
    },
    beforeRouteLeave(to, from, next) {
      if (this.copiedTarget) {
        if (this.copiedTarget.activation_status === ACTIVATION_STATUS.DRAFT) {
          next()
          deleteTarget(this.copiedTarget)
            .then(function (response) {
              console.log(response)
            })
            .catch(function (err) {
              console.log(err)
            })
            .finally(() => {
              return
            })
        } else if (
          this.copiedTarget.activation_status === ACTIVATION_STATUS.LIVE ||
          this.copiedTarget.activation_status === ACTIVATION_STATUS.FAILED ||
          !this.$store.getters['workbench/saveXandrSegmentEnabled']
        ) {
          next()
          return
        } else {
          this.$refs.saveWorkbenchChangesModal.open(next)
        }
      } else {
        next()
      }
    },
    methods: {
      copyTarget() {
        this.copiedTarget = cloneDeep(
          this.originalTarget === null || !this.originalTarget ? { advertiser: { Name: null } } : this.originalTarget,
        )
        if (this.copiedTarget && !this.copiedTarget.advertiser) {
          this.copiedTarget.advertiser = { Name: null }
        }
        if (!this.copiedTarget.destinations) {
          this.copiedTarget.destinations = []
        }
        if (this.showInsights && !this.copiedTarget.xandr_data) {
          this.copiedTarget.xandr_data = { frequency: null }
        }
      },
      deleteTarget() {
        this.$store.dispatch(SET_CURRENT_TARGET, cloneDeep(this.copiedTarget))
        this.$store.dispatch(DELETE_CURRENT_TARGET)
      },
      setViewOnly() {
        // TODO_AFTER
        // Since all of these are pertaining to the Xandr View only we might have to handle this in the store instead, through the interface
        // since a lot of the model has changed we will need to change how the target is handled in the UI
        // I will be setting the view only to false for now and we can come back to it later
        return false
        // const isLASAndLiveOrFailed = !this.isSDTarget && (this.isFailed || this.isLive)
        // const isModeled = this.isModeling || this.isModeled || this.isModeledFailed
        // this.viewOnly = this.isPendingApproval || this.isApproved || this.isSubmitted || !this.hasPermissions || isLASAndLiveOrFailed || this.isCancelled || isModeled
      },
    },
    watch: {
      originalTarget() {
        this.copyTarget()
        this.setViewOnly()
      },
      hasPermissions() {
        this.setViewOnly()
      },
    },
  }
</script>

<style>
  .converged-workbench {
    height: 100%;
  }
</style>



// WEBPACK FOOTER //
// WorkbenchConvergence.vue?319c84dd